import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import TourRequestForm from "@/components/TourRequestForm";
import SEO from "@/components/SEO";

const contactInfo = [
  {
    icon: MapPin,
    title: "Visit Us",
    content: "1790 Highway A1A, Suite 209\nSatellite Beach, FL 32937",
    link: "https://maps.google.com/?q=1790+Highway+A1A+Suite+209+Satellite+Beach+FL+32937",
    linkText: "Get Directions",
  },
  {
    icon: Phone,
    title: "Call Us",
    content: "(321) 866-8572",
    link: "tel:321-866-8572",
    linkText: "Call Now",
  },
  {
    icon: Mail,
    title: "Email Us",
    content: "workcoastal@gmail.com",
    link: "mailto:workcoastal@gmail.com",
    linkText: "Send Email",
  },
  {
    icon: Clock,
    title: "Hours",
    content: "Monday - Friday\n8:00 AM - 6:00 PM",
    link: null,
    linkText: null,
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const ContactPage = () => {
  return (
    <div className="pt-20" data-testid="contact-page">
      <SEO
        title="Contact Coastal Collaborative | Schedule a Tour in Satellite Beach FL"
        description="Schedule a tour of our Satellite Beach coworking space and office rentals. Located at 1790 Highway A1A, Suite 209. Call (321) 866-8572 or request a tour online."
        canonical="https://www.workcoastal.com/contact"
      />
      {/* Hero Section */}
      <section className="py-20 bg-brand" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block font-sans text-sm font-medium text-sea-light uppercase tracking-wider mb-4">
              Contact Us
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Request a Tour
            </h1>
            <p className="font-sans text-lg text-white/80 leading-relaxed">
              Ready to see our space? Fill out the form below and we'll get back to you 
              within 24 hours to schedule your visit.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white border-b border-sand/30" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                className="bg-sand-light/30 rounded-2xl p-6 text-center card-hover"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={`contact-info-${info.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mx-auto mb-4 shadow-sm">
                  <info.icon className="w-6 h-6 text-sea" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-lg font-semibold text-brand mb-2">
                  {info.title}
                </h3>
                <p className="font-sans text-sm text-muted-foreground whitespace-pre-line mb-3">
                  {info.content}
                </p>
                {info.link && (
                  <a
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="font-sans text-sm font-medium text-sea hover:text-sea-light transition-colors"
                  >
                    {info.linkText}
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-3xl font-bold text-brand mb-4">
                Schedule Your Tour
              </h2>
              <p className="font-sans text-muted-foreground leading-relaxed mb-8">
                Fill out the form below and we'll contact you within 24 hours to 
                schedule your visit. No pressure, just come see the space!
              </p>
              <TourRequestForm />
            </motion.div>

            {/* Map / Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="bg-sand-light/30 rounded-2xl p-8">
                <h3 className="font-serif text-xl font-semibold text-brand mb-4">
                  Our Location
                </h3>
                <p className="font-sans text-muted-foreground leading-relaxed mb-4">
                  We're located on the 2nd floor of the plaza at 1790 Highway A1A in 
                  Satellite Beach. Look for Suite 209 – there's a restaurant downstairs 
                  and plenty of parking available.
                </p>
                <p className="font-sans text-muted-foreground leading-relaxed">
                  Just steps from the beach, our oceanview workspace offers the perfect 
                  blend of productivity and relaxation. Come see why our members love 
                  working here!
                </p>
              </div>

              {/* Google Maps Embed */}
              <div className="rounded-2xl overflow-hidden h-[300px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3511.073193655743!2d-80.5950744!3d28.1767244!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88de0f8c3b8f8c3d%3A0x8c3b8f8c3b8f8c3d!2s1790%20FL-A1A%2C%20Satellite%20Beach%2C%20FL%2032937!5e0!3m2!1sen!2sus!4v1635000000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Coastal Collaborative Location"
                  data-testid="contact-map"
                />
              </div>

              <div className="bg-brand rounded-2xl p-8 text-white">
                <h3 className="font-serif text-xl font-semibold mb-3">
                  What to Expect
                </h3>
                <ul className="space-y-2 font-sans text-sm text-white/80">
                  <li>• A friendly 15-20 minute tour of our space</li>
                  <li>• See our private offices, coworking area, and conference room</li>
                  <li>• Learn about membership options and pricing</li>
                  <li>• Meet some of our current members</li>
                  <li>• No pressure – just come see if we're the right fit!</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
